import React, { useEffect, useState } from "react";
import SaveButton from "../../../components/Buttons/SaveButton";
import NumberField from "../../../components/Fields/NumberField";
import SelectListField from "../../../components/Fields/SelectListField";
import TextboxField from "../../../components/Fields/TextboxField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import { useNotifications } from "../../../contexts/notificationContext";
import CodeTaskParameterInfo from "../../../models/CodeTaskParameterInfo";
import DataService from "../../../services/data.service";
import { PopupProps } from "../../../types/PopupProps";
import { DataResult } from "../../../types/Result";
import CodeTaskField from "../Controls/CodeTaskField";

interface Props extends PopupProps {
    workflowActionId,
}

interface WorkflowActionCodeTaskCreateModel {
    workflowActionId: number
    codeTaskName: string
    parameters?: string
    executionOrder: number
    codeTaskParameterInfos: CodeTaskParameterInfo[]
}

const AddWorkflowActionCodeTask: React.FC<Props> = ({ show, onHide, workflowActionId }) => {
    const codeTaskNames = useSelectListOptions('api/v1/selectlist/CodeTaskNames', '-- Select codetask handler');
    const [serverErrors, setServerErrors] = useState<DataResult<WorkflowActionCodeTaskCreateModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();

    const [formData, setFormData] = useState<WorkflowActionCodeTaskCreateModel>({
        workflowActionId: workflowActionId,
        codeTaskName: '',
        executionOrder: 0,
        codeTaskParameterInfos: []
    });

    useEffect(() => {
        getCodeTaskParameterInfo(formData?.codeTaskName)
    }, [formData?.codeTaskName])

    function getCodeTaskParameterInfo(codeTaskName?: string) {
        if (formData && codeTaskName) {
            DataService
                .post<CodeTaskParameterInfo[]>(`api/v1/CodeTaskParameterInfos/GetByCodeTaskName`, {codeTaskName: codeTaskName})
                .then(response => {
                    formData.codeTaskParameterInfos = response
                    setFormData(() => { return { ...formData } })
                })
                .catch(e => handleException(e));
        }
    }

    function submit() {
        if (formData) {
            DataService
                .create<WorkflowActionCodeTaskCreateModel>(`api/v1/WorkflowActionCodeTasks/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        onHide()
                })
                .catch(e => handleException(e));
        }
    }

    function onFieldChanged(name: string, value: string): void {
        if (formData) {
            name.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setFormData(() => { return { ...formData } });
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, formData);
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'executionOrder', label: 'Execution order' },
            { name: 'codeTaskName', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'parameters', label: 'Parameters' },
        ],
        handleSubmit: submit,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    if (formData?.codeTaskParameterInfos) {
        formData.codeTaskParameterInfos.forEach((parInfo, index) => {
            formConfig.fields.push({ name: `codeTaskParameterInfos.${index}.value`, label: parInfo.name })
        })
    }

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    return (
        <Popup show={show} onHide={onHide} size={'lg'} title={'Add action codetask'} >
            <Popup.Body>
                <NumberField {...getFieldProps('executionOrder')} />
                <SelectListField {...getFieldProps('codeTaskName')} options={codeTaskNames} />
                <TextboxField {...getFieldProps('parameters')} />
                {
                    formData.codeTaskParameterInfos.map((parInfo, index) =>
                        <CodeTaskField {...getFieldProps(`codeTaskParameterInfos.${index}.value`)} codeTaskParamterInfo={parInfo} />
                    )
                }
            </Popup.Body>
            <Popup.Footer>
                <SaveButton {...getSubmitButtonProps()} text='Add' />
            </Popup.Footer>
        </Popup>
    );
}
export default AddWorkflowActionCodeTask;

